import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSbpSessionStatus, SbpSessionResponse } from "../../Api";
import { STORAGE_KEYS } from "../../constants";
import { getStorageItem, setStorageItem } from "../../utils";
import Spinner from "../../components/Spinner";
import styles from "./styles.module.css";

interface StoredSbpSession {
  sessionId: string;
  playerId: number;
  productId: string;
  timestamp: number;
}

interface StoredCardSession {
  playerId: number;
  productId: string;
  timestamp: number;
}

export function PaymentStatus() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<SbpSessionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentType, setPaymentType] = useState<'sbp' | 'card' | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Check for both sessions - prioritize the most recent one
    const storedSbpSessionStr = getStorageItem(STORAGE_KEYS.sbpSession);
    const storedCardSessionStr = getStorageItem(STORAGE_KEYS.cardSession);
    
    let sbpTimestamp = 0;
    let cardTimestamp = 0;
    
    if (storedSbpSessionStr) {
      try {
        const sbpSession = JSON.parse(storedSbpSessionStr) as StoredSbpSession;
        sbpTimestamp = sbpSession.timestamp;
      } catch {
        // Invalid session, will be ignored
      }
    }
    
    if (storedCardSessionStr) {
      try {
        const cardSession = JSON.parse(storedCardSessionStr) as StoredCardSession;
        cardTimestamp = cardSession.timestamp;
      } catch {
        // Invalid session, will be ignored
      }
    }
    
    // Use the most recent session
    if (sbpTimestamp > cardTimestamp && storedSbpSessionStr) {
      // Clear old card session
      if (storedCardSessionStr) {
        console.log('[PaymentStatus] Clearing old card session in favor of newer SBP session');
        localStorage.removeItem(STORAGE_KEYS.cardSession);
      }
      setPaymentType('sbp');
      handleSbpPayment(storedSbpSessionStr);
    } else if (cardTimestamp > 0 && storedCardSessionStr) {
      // Clear old SBP session
      if (storedSbpSessionStr) {
        console.log('[PaymentStatus] Clearing old SBP session in favor of newer card session');
        localStorage.removeItem(STORAGE_KEYS.sbpSession);
      }
      setPaymentType('card');
      handleCardPayment(storedCardSessionStr);
    } else {
      setError("Сессия оплаты не найдена. Возможно, время ожидания истекло.");
      setIsLoading(false);
    }

    return () => {
      stopPolling();
    };
  }, []);

  const handleCardPayment = (storedSessionStr: string) => {
    let storedSession: StoredCardSession;
    try {
      storedSession = JSON.parse(storedSessionStr) as StoredCardSession;
    } catch {
      setError("Ошибка чтения данных сессии.");
      setIsLoading(false);
      return;
    }

    // Check if session is not too old (30 minutes for card payments)
    const thirtyMinutes = 30 * 60 * 1000;
    if (Date.now() - storedSession.timestamp > thirtyMinutes) {
      // Don't show error - payment might have succeeded
      // Just show neutral message
      setIsLoading(false);
      localStorage.removeItem(STORAGE_KEYS.cardSession);
      setStorageItem(STORAGE_KEYS.lastPlayerId, String(storedSession.playerId));
      return;
    }

    // For card payments, we can't poll for status, so show a processing message
    // and clear the session after showing it for a while
    setIsLoading(false);
    
    // Auto-clear card session after showing message
    setTimeout(() => {
      localStorage.removeItem(STORAGE_KEYS.cardSession);
      setStorageItem(STORAGE_KEYS.lastPlayerId, String(storedSession.playerId));
    }, 5000); // Increased to 5 seconds to give user time to read
  };

  const handleSbpPayment = (storedSessionStr: string) => {
    let storedSession: StoredSbpSession;
    try {
      storedSession = JSON.parse(storedSessionStr) as StoredSbpSession;
    } catch {
      setError("Ошибка чтения данных сессии.");
      setIsLoading(false);
      return;
    }

    // Check if session is not too old (20 minutes as per SBP spec)
    const twentyMinutes = 20 * 60 * 1000;
    if (Date.now() - storedSession.timestamp > twentyMinutes) {
      setError("Время ожидания оплаты истекло. Пожалуйста, попробуйте снова.");
      setIsLoading(false);
      return;
    }

    // Start polling for payment status
    const checkStatus = async () => {
      const sessionStatus = await getSbpSessionStatus(storedSession.sessionId);
      
      if (!sessionStatus) {
        setError("Не удалось проверить статус оплаты. Проверьте подключение.");
        stopPolling();
        setIsLoading(false);
        return;
      }

      setStatus(sessionStatus);
      setIsLoading(false);

      // Stop polling on final status
      if (sessionStatus.status === "paid") {
        stopPolling();
        setStorageItem(STORAGE_KEYS.lastPlayerId, String(storedSession.playerId));
        // Clear the session from storage on success
        localStorage.removeItem(STORAGE_KEYS.sbpSession);
      } else if (sessionStatus.status === "failed") {
        stopPolling();
        // Clear the session from storage on failure
        localStorage.removeItem(STORAGE_KEYS.sbpSession);
      }
    };

    // Initial check
    checkStatus();

    // Poll every 2 seconds if not in final state
    pollingRef.current = setInterval(checkStatus, 2000);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const handleBackToShop = () => {
    navigate("/gifts");
  };

  // Card payment - show processing message
  if (paymentType === 'card' && !error) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.spinnerContainer}>
            <Spinner />
          </div>
          <h2 className={styles.title}>Обработка платежа...</h2>
          <p className={styles.description}>
            Ваш платеж обрабатывается банком. Награды будут зачислены в игру в течение нескольких минут.
          </p>
          <p className={styles.description}>
            Проверьте игру через несколько минут. Если награды не появились, проверьте статус транзакции в вашем банке или обратитесь в поддержку.
          </p>
          <button className={styles.button} onClick={handleBackToShop}>
            Вернуться в магазин
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.spinnerContainer}>
            <Spinner />
          </div>
          <h2 className={styles.title}>Проверяем статус оплаты...</h2>
          <p className={styles.description}>
            Пожалуйста, подождите. Это может занять несколько секунд.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.iconError}>❌</div>
          <h2 className={styles.title}>Ошибка</h2>
          <p className={styles.description}>{error}</p>
          <button className={styles.button} onClick={handleBackToShop}>
            Вернуться в магазин
          </button>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.iconError}>⚠️</div>
          <h2 className={styles.title}>Статус недоступен</h2>
          <p className={styles.description}>
            Не удалось получить статус оплаты. Попробуйте обновить страницу.
          </p>
          <button className={styles.button} onClick={handleBackToShop}>
            Вернуться в магазин
          </button>
        </div>
      </div>
    );
  }

  // Payment successful
  if (status.status === "paid") {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.iconSuccess}>✓</div>
          <h2 className={styles.title}>Оплата прошла успешно!</h2>
          <p className={styles.description}>
            Спасибо за покупку! Награды будут зачислены в игру в течение нескольких минут.
          </p>
          <button className={styles.button} onClick={handleBackToShop}>
            Вернуться в магазин
          </button>
        </div>
      </div>
    );
  }

  // Payment failed
  if (status.status === "failed") {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.iconError}>❌</div>
          <h2 className={styles.title}>Оплата не прошла</h2>
          <p className={styles.description}>
            К сожалению, платёж не был завершён. Пожалуйста, попробуйте снова.
          </p>
          <button className={styles.button} onClick={handleBackToShop}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  // Payment pending (awaiting_payment or pending)
  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
        <h2 className={styles.title}>Ожидаем подтверждение оплаты...</h2>
        <p className={styles.description}>
          Пожалуйста, завершите оплату в приложении банка. 
          Статус будет обновлён автоматически.
        </p>
        <div className={styles.statusInfo}>
          <p>Время до истечения сессии: {Math.floor(status.expiresInSeconds / 60)} мин.</p>
        </div>
        <button className={styles.buttonSecondary} onClick={handleBackToShop}>
          Отменить и вернуться
        </button>
      </div>
    </div>
  );
}
